<script setup lang="ts">
import { router, useForm } from "@inertiajs/vue3"
import AppShell from "../Layout/AppShell.vue"
import { UiButton, UiCurrencyInput, UiFormLayout, UiSelect, UiTextInput } from "@/components"
import { useConfirmation } from "@/composables/useConfirmation"
import { useNotifications } from "@/composables/useNotifications"
import { formatDate, toDateInputValue } from "@/utils/date"

const { confirm } = useConfirmation()
const notify = useNotifications()

type SupplierPriceRow = {
  id: number
  supplier_name: string
  unit_price: string
  currency: string
  valid_from: string
  valid_to: string
  source: string
}

const props = defineProps<{
  product: {
    id: number
    name: string
    sku: string
    company: string
  }
  supplier_prices: SupplierPriceRow[]
  suppliers: Array<{ label: string; value: number }>
  sources: Array<{ label: string; value: string }>
  errors?: Record<string, string[]>
}>()

const form = useForm({
  supplier_price: {
    supplier_id: "" as number | "",
    unit_price_cents: "0",
    currency: "BRL",
    valid_from: toDateInputValue(),
    valid_to: "",
    source: "manual",
  },
})

const fieldError = (field: string) => props.errors?.[field]?.[0]

function submit() {
  form.post(`/products/${props.product.id}/supplier_prices`, {
    onSuccess: () => notify.success("Preco adicionado com sucesso"),
  })
}

async function destroyPrice(id: number) {
  const ok = await confirm({
    title: "Excluir preco",
    message: "Deseja excluir este preco?",
    type: "danger",
    confirmText: "Excluir",
  })
  if (!ok) return

  router.delete(`/products/${props.product.id}/supplier_prices/${id}`, {
    preserveScroll: true,
    onSuccess: () => notify.success("Preco excluido com sucesso"),
  })
}
</script>

<template>
  <AppShell>
    <div class="space-y-6">
      <UiFormLayout
        title="Precos por fornecedor"
        :subtitle="`Precos de compra por fornecedor para ${product.name} (${product.sku})`"
        back-href="/products"
        back-label="Voltar para produtos"
      >
        <div class="rounded-md bg-slate-50 p-3 text-sm text-slate-700">Empresa: {{ product.company }}</div>

        <form class="mt-4 grid gap-4 md:grid-cols-3" @submit.prevent="submit">
          <UiSelect
            id="supplier-price-supplier"
            :model-value="form.supplier_price.supplier_id"
            label="Fornecedor"
            :options="suppliers"
            :error="fieldError('supplier') || fieldError('supplier_id')"
            @update:model-value="form.supplier_price.supplier_id = $event as number | ''"
          />

          <UiCurrencyInput
            id="supplier-price-value"
            v-model="form.supplier_price.unit_price_cents"
            label="Preco do fornecedor"
            :error="fieldError('unit_price')"
          />

          <UiSelect id="supplier-price-source" v-model="form.supplier_price.source" label="Origem" :options="sources" :error="fieldError('source')" />

          <UiTextInput
            id="supplier-price-currency"
            v-model="form.supplier_price.currency"
            label="Moeda"
            placeholder="BRL"
            :error="fieldError('currency')"
          />

          <div>
            <label class="mb-1 block text-sm font-medium text-slate-700" for="supplier-price-valid-from">Valido de</label>
            <input
              id="supplier-price-valid-from"
              v-model="form.supplier_price.valid_from"
              type="date"
              class="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900"
            />
            <p v-if="fieldError('valid_from')" class="mt-1 text-sm text-rose-600">{{ fieldError('valid_from') }}</p>
          </div>

          <div>
            <label class="mb-1 block text-sm font-medium text-slate-700" for="supplier-price-valid-to">Valido ate</label>
            <input
              id="supplier-price-valid-to"
              v-model="form.supplier_price.valid_to"
              type="date"
              class="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900"
            />
            <p v-if="fieldError('valid_to')" class="mt-1 text-sm text-rose-600">{{ fieldError('valid_to') }}</p>
          </div>

          <div class="md:col-span-3">
            <UiButton type="submit" :disabled="form.processing">Adicionar preco</UiButton>
          </div>
        </form>
      </UiFormLayout>

      <section class="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <h2 class="mb-4 text-lg font-semibold text-slate-900">Historico de precos</h2>

        <div class="overflow-x-auto">
          <table class="min-w-full border-collapse">
            <thead>
              <tr class="border-b border-slate-200">
                <th class="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Fornecedor</th>
                <th class="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Preco fornecedor</th>
                <th class="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Moeda</th>
                <th class="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Valido de</th>
                <th class="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Valido ate</th>
                <th class="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Origem</th>
                <th class="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">Acoes</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="supplier_prices.length === 0">
                <td colspan="7" class="px-3 py-8 text-center text-sm text-slate-500">Nenhum preco cadastrado.</td>
              </tr>

              <tr v-for="price in supplier_prices" :key="price.id" class="border-b border-slate-100">
                <td class="px-3 py-2 text-sm text-slate-700">{{ price.supplier_name }}</td>
                <td class="px-3 py-2 text-sm text-slate-700">{{ price.unit_price }}</td>
                <td class="px-3 py-2 text-sm text-slate-700">{{ price.currency }}</td>
                <td class="px-3 py-2 text-sm text-slate-700">{{ formatDate(price.valid_from) }}</td>
                <td class="px-3 py-2 text-sm text-slate-700">{{ formatDate(price.valid_to) }}</td>
                <td class="px-3 py-2 text-sm text-slate-700">{{ price.source }}</td>
                <td class="px-3 py-2 text-right text-sm text-slate-700">
                  <UiButton size="sm" variant="danger" @click="destroyPrice(price.id)">Excluir</UiButton>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  </AppShell>
</template>

